/**
 * Investor Metrics — scorecard with 4 metric categories
 *
 * Route: /admin/reporting/investors/:id (index)
 */

import { useState, useMemo, useCallback } from 'react'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { DatePicker } from '@/common/components/date-picker'
import { useInvestorMetrics } from '@/features/reporting/hooks/useReportingData'
import { semantic } from '@/features/reporting/chartPalette'
import * as styles from '../../index.css'

export const Route = createFileRoute({
  component: InvestorMetricsPage,
})

// =============================================================================
// Types
// =============================================================================

type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

interface MetricCardData {
  label: string
  value: string
  delta?: number | null
  comingSoon?: boolean
}

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function formatPct(value: number | null): string {
  if (value === null) return 'N/A'
  return `${value.toFixed(1)}%`
}

function formatDays(value: number | null): string {
  if (value === null) return 'N/A'
  return `${value.toFixed(1)} days`
}

function calcDelta(current: number, prior: number): number | null {
  if (prior === 0) return null
  return ((current - prior) / prior) * 100
}

function deltaColor(delta: number | null): string {
  if (delta === null) return semantic.neutral
  if (delta > 0) return semantic.positive
  if (delta < 0) return semantic.negative
  return semantic.neutral
}

function deltaLabel(delta: number | null): string {
  if (delta === null) return 'N/A'
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function deltaIndicator(delta: number | null): string {
  if (delta === null) return ''
  if (delta > 0) return '\u25B2 '
  if (delta < 0) return '\u25BC '
  return ''
}

// =============================================================================
// Metric Card Component
// =============================================================================

function MetricCard({ label, value, delta, comingSoon }: MetricCardData) {
  return (
    <div className={styles.metricCard} style={{ cursor: 'default' }}>
      <Text variant="bodySm" color="muted">{label}</Text>
      {comingSoon ? (
        <div style={{ margin: '4px 0' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            backgroundColor: '#f0f0f0',
            color: '#888',
            fontSize: '13px',
            fontWeight: 500,
          }}>
            Coming Soon
          </span>
        </div>
      ) : (
        <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{value}</Text>
      )}
      {delta !== undefined && !comingSoon && (
        <Text variant="bodyXs" style={{ color: deltaColor(delta ?? null) }}>
          {deltaIndicator(delta ?? null)}
          {deltaLabel(delta ?? null)} vs prior period
        </Text>
      )}
    </div>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <Text variant="body" weight="bold">{title}</Text>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function InvestorMetricsPage() {
  const { id } = Route.useParams()
  const [dateRange, setDateRange] = useState<DateRangePreset>('1y')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  const { data: metrics, isLoading: metricsLoading } = useInvestorMetrics(id, startDateStr, endDateStr)

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange])

  // Build metric cards
  const activityCards: MetricCardData[] = [
    { label: 'Total Inquiries', value: formatCount(metrics?.activity.total_inquiries ?? 0), delta: metrics ? calcDelta(metrics.activity.total_inquiries, metrics.activity.total_inquiries_prior) : null },
    { label: 'Total Offers', value: formatCount(metrics?.activity.total_offers ?? 0), delta: metrics ? calcDelta(metrics.activity.total_offers, metrics.activity.total_offers_prior) : null },
    { label: 'Time to First Action', value: formatDays(metrics?.activity.time_to_first_action_days ?? null) },
    { label: 'Listings Viewed', value: '', comingSoon: true },
    { label: 'Inquiry-to-View Ratio', value: '', comingSoon: true },
  ]

  const offerQualityCards: MetricCardData[] = [
    { label: 'Offer vs. Floor Price', value: formatPct(metrics?.offer_quality.avg_offer_vs_floor_pct ?? null) },
    { label: 'Variance from Winning Offer', value: '', comingSoon: true },
    { label: 'Contingency Profile', value: '', comingSoon: true },
  ]

  const reliabilityCards: MetricCardData[] = [
    { label: 'Contracts UC', value: formatCount(metrics?.reliability.contracts_uc ?? 0) },
    { label: 'Closed', value: formatCount(metrics?.reliability.closed ?? 0) },
    { label: 'Contract-to-Close Rate', value: formatPct(metrics?.reliability.contract_to_close_rate ?? null) },
    { label: 'Fall-Through Rate', value: formatPct(metrics?.reliability.fall_through_rate ?? null) },
    { label: 'Avg Days to Close', value: '', comingSoon: true },
    { label: 'Retrade Rate', value: '', comingSoon: true },
    { label: 'Avg Retrade Amount', value: '', comingSoon: true },
  ]

  const financialsCards: MetricCardData[] = [
    { label: 'Proof of Funds', value: metrics?.financials.proof_of_funds ? 'Yes' : 'No' },
    { label: 'Transaction Volume LTM', value: '', comingSoon: true },
    { label: 'Average Purchase Price', value: '', comingSoon: true },
  ]

  return (
    <>
      {/* Time Period Filter */}
      <div className={styles.controls}>
        <div className={styles.controlDateGroup}>
          <div className={styles.granularityToggle}>
            {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
              <button
                key={preset}
                className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)}
                onClick={() => handleDateRangeChange(preset)}
              >
                {preset === 'custom' ? 'Custom' : preset}
              </button>
            ))}
          </div>
          <div className={styles.customDateRange}>
            <div className={styles.customDatePicker}>
              <DatePicker label="Start" value={new Date(startDateStr)} onChange={setCustomStart} maxDate={new Date(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
            </div>
            <Text variant="bodySm" color="muted">{'\u2013'}</Text>
            <div className={styles.customDatePicker}>
              <DatePicker label="End" value={new Date(endDateStr)} onChange={setCustomEnd} minDate={new Date(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
            </div>
          </div>
        </div>
      </div>

      {metricsLoading && (
        <Stack gap="16" align="center" justify="center" style={{ minHeight: '200px' }}>
          <LoadingSpinner size="sm" />
        </Stack>
      )}

      {!metricsLoading && (
        <>
          <SectionHeader title="Activity & Engagement" />
          <div className={styles.metricsGrid}>
            {activityCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Offer Quality" />
          <div className={styles.metricsGrid}>
            {offerQualityCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Reliability" />
          <div className={styles.metricsGrid}>
            {reliabilityCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Financials" />
          <div className={styles.metricsGrid}>
            {financialsCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>
        </>
      )}
    </>
  )
}
